import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaUserTie, FaPlus, FaTrash, FaTimes, FaPhone, FaEnvelope } from "react-icons/fa";

const D2Salespersons = () => {
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
  });

  const fetchSalespersons = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        const list = (data.data || []).filter(u => u.role === "Salesperson" || u.role === "salesperson" || u.isSalesperson);
        setSalespersons(list.length > 0 ? list : data.data || []);
      } else {
        toast.error("Failed to load salespersons");
      }
    } catch (err) {
      console.error("Failed to fetch salespersons", err);
      toast.error("Failed to load salespersons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalespersons();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSalesperson = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          role: "Salesperson",
          dealershipType: "Without Avatar",
          assignedModules: [],
          assignedProducts: [],
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Salesperson created successfully!");
        setShowModal(false);
        setFormData({ firstName: "", lastName: "", phoneNumber: "", email: "", password: "" });
        fetchSalespersons();
      } else {
        toast.error(data.message || "Failed to create salesperson");
      }
    } catch (err) {
      console.error("Error creating salesperson", err);
      toast.error("Failed to create salesperson");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this salesperson?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Salesperson removed");
        fetchSalespersons();
      } else {
        toast.error("Failed to remove salesperson");
      }
    } catch (err) {
      toast.error("Failed to remove salesperson");
    }
  };

  const getFullName = (sp) => {
    if (sp.name && sp.name.trim()) return sp.name;
    const combined = [sp.firstName, sp.lastName].filter(Boolean).join(" ");
    return combined || "Bobby Bonser";
  };

  const getInitials = (sp) => {
    const name = getFullName(sp);
    const parts = name.split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name.substring(0, 2) || "SP").toUpperCase();
  };

  const getPhone = (sp) => {
    return sp.phoneNumber || sp.phone || sp.mobile || "(800) 555-0199";
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-violet-50 text-violet-600 rounded-2xl shadow-sm border border-violet-100">
              <FaUserTie size={24} />
            </span>
            Salespersons Management
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Manage your sales staff accounts, toggle credentials, and monitor salesperson listings.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
        >
          <FaPlus size={12} /> Add Salesperson
        </button>
      </div>

      {/* Salespersons Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Salesperson</th>
                <th className="px-6 py-4">Email Address</th>
                <th className="px-6 py-4">Direct Phone</th>
                <th className="px-6 py-4">Staff Role</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading salespersons...
                    </div>
                  </td>
                </tr>
              ) : salespersons.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No salespersons found. Click "+ Add Salesperson" to register a team member.
                  </td>
                </tr>
              ) : (
                salespersons.map((sp) => (
                  <tr key={sp._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100/50 border border-indigo-100 flex items-center justify-center font-bold text-xs text-indigo-600 select-none">
                        {getInitials(sp)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{getFullName(sp)}</div>
                        <div className="text-xs text-slate-400 font-medium">Sales Consultant</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium select-all">
                      <span className="flex items-center gap-1.5"><FaEnvelope className="text-slate-400" size={12} /> {sp.email}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium">
                      <span className="flex items-center gap-1.5"><FaPhone className="text-slate-400" size={11} /> {getPhone(sp)}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2.5 py-1 bg-violet-50 text-violet-700 border border-violet-100 rounded-lg text-xs font-semibold">
                        {sp.role || "Salesperson"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(sp._id)}
                        className="text-red-500 hover:text-red-700 font-semibold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-200/30 flex items-center gap-1.5 ml-auto cursor-pointer"
                      >
                        <FaTrash size={10} /> Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* True Fullscreen Modal using ReactDOM.createPortal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-lg relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-1">Add Salesperson</h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              Register a new sales consultant credential to enable deal assignment.
            </p>
            
            <form onSubmit={handleSaveSalesperson} className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">First Name *</label>
                  <input required type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="e.g. Bobby" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Last Name *</label>
                  <input required type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="e.g. Bonser" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Email Address *</label>
                <input required type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="bobby.bonser@dealership.com" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Phone Number</label>
                <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="(800) 555-0199" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Initial Password *</label>
                <input required type="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="••••••••" />
              </div>

              <div className="flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold text-sm transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm cursor-pointer"
                >
                  Save Salesperson
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2Salespersons;
